/**
 * Slot conflicts: the client-side mirror of the booking double-book guard the
 * DATABASE enforces, so slot pickers only offer slots the write will accept.
 *
 * The rule (keep in lockstep with the migrations):
 *   - `is_firm` (db/migrations/20260901121022_spine_tables.sql, app.bookings):
 *       status IN ('confirmed','checked-in','in-chair','completed')
 *       AND overbookable IS NOT TRUE
 *   - `bookings_artist_no_double_book`
 *     (db/migrations/20260903000000_bookings_exclude_double_book.sql):
 *       EXCLUDE USING gist (artist_id WITH =, period WITH &&) WHERE (is_firm)
 *     with period = tstzrange(start, start + duration_min, '[)').
 *
 * So: two FIRM bookings may not overlap on the SAME ARTIST (any studio — the
 * constraint has no studio column). Ranges are half-open, so back-to-back
 * (end == next start) is fine, and an empty range (zero length) never
 * overlaps anything. Held / pending-deposit / rescheduled / cancelled /
 * no-show / superseded rows and overbookable ghosts never block and are never
 * blocked. A violation surfaces as SQLSTATE 23P01 -> BookingConflictError
 * (packages/db/src/repositories.ts) -> HTTP 409.
 *
 * The exclusion is partial on BOTH rows: a non-firm write is never rejected.
 * `overlapsFirm` treats the candidate as the firm booking it will become
 * (a rebook is written confirmed; a paid hold is promoted to confirmed); a
 * caller writing a non-firm row can skip the check via `isFirm`.
 */

/** Statuses the `is_firm` generated column counts. */
export const FIRM_BOOKING_STATUSES: ReadonlySet<string> = new Set([
  'confirmed',
  'checked-in',
  'in-chair',
  'completed',
]);

/** The booking fields the conflict rule reads (web `Booking` fits structurally). */
export interface SlotBooking {
  id: string;
  artistId: string;
  /** ISO instant. */
  start: string;
  durationMin: number;
  status: string;
  overbookable?: boolean | null;
}

/** A slot being considered for `artistId`, as a half-open [startIso, endIso). */
export interface SlotCandidate {
  artistId: string;
  startIso: string;
  endIso: string;
  /** The booking being moved: the DB never compares a row with itself. */
  excludeBookingId?: string;
}

export type BookingCollection<B> = readonly B[] | Readonly<Record<string, B>>;

/** Mirror of `is_firm`: a firm status AND `overbookable IS NOT TRUE`. */
export function isFirm(b: Pick<SlotBooking, 'status' | 'overbookable'>): boolean {
  return FIRM_BOOKING_STATUSES.has(b.status) && b.overbookable !== true;
}

function instant(iso: string, what: string): number {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) throw new RangeError(`slotConflicts: unparseable ${what} "${iso}"`);
  return ms;
}

function toList<B>(bookings: BookingCollection<B>): readonly B[] {
  return Array.isArray(bookings) ? bookings : Object.values(bookings as Record<string, B>);
}

/**
 * The first firm booking the candidate would collide with on the DB exclusion
 * constraint, or null when the write would be accepted.
 *
 * Throws RangeError for an unparseable candidate or one that ends before it
 * starts (Postgres rejects that range as well) rather than offering it.
 */
export function overlapsFirm<B extends SlotBooking>(
  candidate: SlotCandidate,
  bookings: BookingCollection<B>,
): B | null {
  const s = instant(candidate.startIso, 'startIso');
  const e = instant(candidate.endIso, 'endIso');
  if (e < s) throw new RangeError(`slotConflicts: endIso ${candidate.endIso} is before startIso ${candidate.startIso}`);
  if (e === s) return null; // empty range: && is false

  for (const b of toList(bookings)) {
    if (b.id === candidate.excludeBookingId) continue;
    if (b.artistId !== candidate.artistId) continue;
    if (!isFirm(b)) continue;
    const bs = Date.parse(b.start);
    if (Number.isNaN(bs)) continue; // cannot exist in the DB (start is NOT NULL timestamptz)
    const be = bs + b.durationMin * 60_000;
    if (be <= bs) continue; // empty range
    if (bs < e && be > s) return b; // half-open [) overlap
  }
  return null;
}

/** The candidates the DB would accept, in their original order. */
export function filterFreeSlots<C extends SlotCandidate, B extends SlotBooking>(
  candidates: readonly C[],
  bookings: BookingCollection<B>,
): C[] {
  const list = toList(bookings);
  return candidates.filter((c) => overlapsFirm(c, list) === null);
}
